import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

/**
 * Clases base del control de formulario. Exportadas para que otros controles
 * (selects nativos, textareas) puedan reusar el mismo lenguaje visual sin
 * copiar la cadena pieza por pieza (regla DS: reusar > copiar).
 */
export const formControlBaseClassName =
  'flex h-11 w-full rounded-[12px] border border-[#E5E7EB] bg-[#F9FAFB] px-3.5 py-2 text-sm text-foreground placeholder:text-[#9CA3AF] focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-60 transition-colors';

const inputBase = formControlBaseClassName;

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, leadingIcon, trailingIcon, ...props }, ref) => {
    const hasIcons = Boolean(leadingIcon || trailingIcon);

    const paddingOverrides = hasIcons
      ? cn(leadingIcon ? 'pl-10' : null, trailingIcon ? 'pr-10' : null)
      : null;

    const inputElement = (
      <input
        type={type}
        ref={ref}
        className={cn(inputBase, paddingOverrides, hasIcons ? null : className)}
        {...props}
      />
    );

    if (!hasIcons) {
      return inputElement;
    }

    return (
      <div className={cn('relative w-full', className)}>
        {leadingIcon ? (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground [&_svg]:size-4"
          >
            {leadingIcon}
          </span>
        ) : null}
        {inputElement}
        {trailingIcon ? (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground [&_svg]:size-4">
            {trailingIcon}
          </span>
        ) : null}
      </div>
    );
  },
);
Input.displayName = 'Input';
